/**
 * Account Drawer - Handles open/close, tab switching, and AJAX form submission
 */
(function () {
	"use strict";

	const SELECTORS = {
		drawer: "[data-account-drawer]",
		close: "[data-account-drawer-close]",
		trigger: "[data-account-drawer-trigger]",
		tab: "[data-account-tab]",
		panel: "[data-account-panel]",
		loginForm: "[data-account-login-form]",
		registerForm: "[data-account-register-form]",
		error: "[data-account-error]",
		submit: "[data-account-submit]",
	};

	const CLASSES = {
		active: "is-active",
		visible: "is-visible",
		closing: "is-closing",
	};

	let drawer = null;

	function init() {
		drawer = document.querySelector(SELECTORS.drawer);
		if (!drawer) return;

		bindTriggers();
		bindClose();
		bindTabs();
		bindForms();
	}

	// Open/Close handlers
	function bindTriggers() {
		document.querySelectorAll(SELECTORS.trigger).forEach((btn) => {
			btn.addEventListener("click", (e) => {
				e.preventDefault();
				openDrawer();
			});
		});
	}

	function bindClose() {
		drawer.querySelectorAll(SELECTORS.close).forEach((el) => {
			el.addEventListener("click", closeDrawer);
		});

		// ESC key
		document.addEventListener("keydown", (e) => {
			if (
				e.key === "Escape" &&
				drawer.getAttribute("aria-hidden") === "false"
			) {
				closeDrawer();
			}
		});
	}

	function openDrawer() {
		drawer.setAttribute("aria-hidden", "false");
		document.documentElement.style.overflow = "hidden";

		// Focus first input
		setTimeout(() => {
			const firstInput = drawer.querySelector('input:not([type="hidden"])');
			if (firstInput) firstInput.focus();
		}, 100);
	}

	function closeDrawer() {
		drawer.classList.add(CLASSES.closing);

		const panel = drawer.querySelector(".account-drawer__panel");
		const onTransitionEnd = () => {
			drawer.setAttribute("aria-hidden", "true");
			drawer.classList.remove(CLASSES.closing);
			document.documentElement.style.overflow = "";
			panel.removeEventListener("transitionend", onTransitionEnd);
		};

		panel.addEventListener("transitionend", onTransitionEnd);
	}

	// Tab switching
	function bindTabs() {
		const tabs = drawer.querySelectorAll(SELECTORS.tab);
		const panels = drawer.querySelectorAll(SELECTORS.panel);

		tabs.forEach((tab) => {
			tab.addEventListener("click", () => {
				const target = tab.dataset.accountTab;

				// Update tabs
				tabs.forEach((t) => t.classList.remove(CLASSES.active));
				tab.classList.add(CLASSES.active);

				// Update panels
				panels.forEach((p) => {
					p.classList.toggle(CLASSES.active, p.dataset.accountPanel === target);
				});

				// Clear errors
				drawer.querySelectorAll(SELECTORS.error).forEach((e) => {
					e.classList.remove(CLASSES.visible);
					e.textContent = "";
				});
			});
		});
	}

	// Form handling
	function bindForms() {
		const loginForm = drawer.querySelector(SELECTORS.loginForm);
		const registerForm = drawer.querySelector(SELECTORS.registerForm);

		if (loginForm) {
			// Add return_to field on page load
			ensureReturnToField(loginForm);
			loginForm.addEventListener("submit", (e) =>
				handleFormSubmit(e, loginForm, "login"),
			);
		}

		if (registerForm) {
			ensureReturnToField(registerForm);
			registerForm.addEventListener("submit", (e) =>
				handleFormSubmit(e, registerForm, "register"),
			);
		}
	}

	function ensureReturnToField(form) {
		let returnInput = form.querySelector('input[name="return_to"]');
		if (!returnInput) {
			returnInput = document.createElement("input");
			returnInput.type = "hidden";
			returnInput.name = "return_to";
			form.appendChild(returnInput);
		}
		// Set current page URL
		returnInput.value = window.location.pathname + window.location.search;
	}

	async function handleFormSubmit(e, form, type) {
		// Update return_to with current URL right before submit
		const returnInput = form.querySelector('input[name="return_to"]');
		if (returnInput) {
			returnInput.value = window.location.pathname + window.location.search;
		}

		const submitBtn = form.querySelector(SELECTORS.submit);

		// Just show loading state - let form submit naturally
		submitBtn.disabled = true;
		submitBtn.textContent =
			type === "login" ? "Signing in..." : "Creating account...";

		// Don't prevent default - let the form submit normally to Shopify
		// The return_to field will tell Shopify where to redirect after login
	}

	// ==================== MY GARAGE FUNCTIONALITY ====================

	function initGarage() {
		const garageContainer = document.querySelector("[data-garage-cars]");
		const addBtn = document.querySelector("[data-garage-add]");

		if (!garageContainer) return;

		renderGarageCars();

		if (addBtn) {
			addBtn.addEventListener("click", openGarageSelector);
		}
	}

	// Use the theme's existing garage/vehicle selector
	function openGarageSelector() {
		// Close the account drawer first
		closeDrawer();

		// Use the theme's toggleGaragePopup function
		setTimeout(() => {
			if (typeof window.toggleGaragePopup === "function") {
				window.toggleGaragePopup();
			} else {
				// Fallback: try to find and click the garage button in header
				const garageBtn =
					document.querySelector("[data-garage-trigger]") ||
					document.querySelector(".header-garage-button") ||
					document.querySelector('[onclick*="toggleGaragePopup"]');
				if (garageBtn) {
					garageBtn.click();
				} else {
					console.warn("Garage popup function not found");
				}
			}
		}, 350);
	}

	function getGarageCars() {
		// Use the theme's searchTerms from localStorage (same as the header garage)
		try {
			const stored = localStorage.getItem("searchTerms");
			return stored ? JSON.parse(stored) : [];
		} catch (e) {
			return [];
		}
	}

	function renderGarageCars() {
		const container = document.querySelector("[data-garage-cars]");
		if (!container) return;

		const cars = getGarageCars();

		if (cars.length === 0) {
			container.innerHTML =
				'<p class="account-drawer__garage-empty">No vehicles saved yet</p>';
			return;
		}

		container.innerHTML = cars
			.map((car, index) => {
				const displayName =
					`${car.year || ""} ${car.make || ""} ${car.model || ""}`.trim();
				const collectionHandle = car.ymm
					? car.ymm
							.toLowerCase()
							.trim()
							.replace(/\s+/g, "-")
							.replace(/[^a-z0-9\-]/g, "-")
					: "";

				return `
        <div class="account-drawer__garage-car" data-car-index="${index}">
          <div class="account-drawer__garage-car-header" data-car-toggle="${index}">
            <span class="account-drawer__garage-car-info">${displayName}</span>
            <button type="button" class="account-drawer__garage-car-toggle" data-toggle-btn="${index}" aria-label="Expand options">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
          <div class="account-drawer__garage-car-actions" data-car-actions="${index}">
            <a href="/collections/${collectionHandle}" class="account-drawer__garage-car-action">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              Browse Catalog
            </a>
            <button type="button" class="account-drawer__garage-car-action" data-edit-car="${index}">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit Vehicle
            </button>
            <button type="button" class="account-drawer__garage-car-action account-drawer__garage-car-action--remove" data-remove-car="${index}">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Remove Vehicle
            </button>
          </div>
        </div>
      `;
			})
			.join("");

		// Bind accordion toggles
		container.querySelectorAll("[data-car-toggle]").forEach((header) => {
			header.addEventListener("click", (e) => {
				// Don't toggle if clicking remove button
				if (e.target.closest("[data-remove-car]")) return;

				const idx = header.dataset.carToggle;
				const actions = container.querySelector(`[data-car-actions="${idx}"]`);
				const toggleBtn = container.querySelector(`[data-toggle-btn="${idx}"]`);

				if (actions && toggleBtn) {
					actions.classList.toggle("is-open");
					toggleBtn.classList.toggle("is-open");
				}
			});
		});

		// Bind remove buttons
		container.querySelectorAll("[data-remove-car]").forEach((btn) => {
			btn.addEventListener("click", (e) => {
				e.stopPropagation();
				const idx = e.currentTarget.dataset.removeCar;
				removeCar(idx);
			});
		});

		// Bind edit buttons - opens garage popup
		container.querySelectorAll("[data-edit-car]").forEach((btn) => {
			btn.addEventListener("click", (e) => {
				e.stopPropagation();
				openGarageSelector();
			});
		});
	}

	function removeCar(index) {
		// Use the theme's GarageLogic if available
		if (
			window.GarageLogic &&
			typeof window.GarageLogic.removeVehicleFromLocalStorage === "function"
		) {
			const cars = getGarageCars();
			if (cars[index]) {
				window.GarageLogic.removeVehicleFromLocalStorage(
					cars[index].ymm,
					index.toString(),
				);
				// Refresh the drawer garage after removal
				setTimeout(() => {
					renderGarageCars();
				}, 100);
			}
		} else {
			// Fallback: manually remove from localStorage
			const cars = getGarageCars();
			cars.splice(index, 1);
			setSearchTerms(cars, 15);
			renderGarageCars();
		}
	}

	// Expose for external use
	window.AccountDrawer = {
		open: openDrawer,
		close: closeDrawer,
		openGarage: openGarageSelector,
		refreshGarage: renderGarageCars,
		getCars: getGarageCars,
	};

	// Listen for localStorage changes to refresh garage in real-time (other tabs)
	window.addEventListener("storage", (e) => {
		if (e.key === "searchTerms") {
			renderGarageCars();
		}
	});

	// Polling to detect localStorage changes in same tab (like main garage does)
	let lastSearchTermsValue = localStorage.getItem("searchTerms");
	setInterval(() => {
		const currentValue = localStorage.getItem("searchTerms");
		if (currentValue !== lastSearchTermsValue) {
			lastSearchTermsValue = currentValue;
			renderGarageCars();
		}
	}, 500);

	// Init on DOM ready
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", () => {
			init();
			initGarage();
		});
	} else {
		init();
		initGarage();
	}
})();
